import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { QrCode, FolderKanban, Users, Eye, Share2, Mail, Headphones, MapPin, Loader2 } from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import { normalizeErrorMessage } from '@/utils/normalizeError';
import AuthPage from '@/pages/AuthPage';

const VALID_AUTH_VALUES = ['login', 'signup', 'forgot-password', 'reset-password'];

const LandingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const authParam = searchParams.get('auth');

  // Derived modal state from URL
  const showAuthModal = authParam === 'login' || authParam === 'signup';
  const authMode = authParam === 'signup' ? 'signup' : 'login';
  const showForgotModal = authParam === 'forgot-password';
  const showResetModal = authParam === 'reset-password';
  const resetToken = searchParams.get('token');

  // Forgot password form state (local, resets on close)
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');

  // Reset password form state (local, resets on close)
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetTokenValid, setResetTokenValid] = useState(null); // null=checking, true=valid, false=invalid

  // Normalize: reset-password without token → clear
  useEffect(() => {
    if (authParam === 'reset-password' && !resetToken) {
      const p = new URLSearchParams(searchParams);
      p.delete('auth');
      p.delete('token');
      setSearchParams(p, { replace: true });
    }
  }, [authParam, resetToken]);
  // Normalize invalid auth param on mount
  useEffect(() => {
    if (authParam && !VALID_AUTH_VALUES.includes(authParam)) {
      const p = new URLSearchParams(searchParams);
      p.delete('auth');
      setSearchParams(p, { replace: true });
    }
  }, [authParam]);

  // Auto-open signup when invited via ?email=
  useEffect(() => {
    if (searchParams.get('email') && !authParam) {
      const p = new URLSearchParams(searchParams);
      p.set('auth', 'signup');
      setSearchParams(p, { replace: true });
    }
  }, []);

  // Reset forgot form state when leaving forgot modal
  useEffect(() => {
    if (!showForgotModal) {
      setForgotEmail('');
      setForgotSent(false);
      setForgotError('');
      setForgotLoading(false);
    }
  }, [showForgotModal]);

  // Reset password form state when leaving reset modal
  useEffect(() => {
    if (!showResetModal) {
      setResetPassword('');
      setResetConfirm('');
      setResetError('');
      setResetLoading(false);
      setResetTokenValid(null);
    }
  }, [showResetModal]);

  // Validate token on reset modal open
  useEffect(() => {
    if (showResetModal && resetToken) {
      setResetTokenValid(null);
      axios.get(`${API}/auth/reset-password/validate`, { params: { token: resetToken } })
        .then(() => setResetTokenValid(true))
        .catch(() => setResetTokenValid(false));
    }
  }, [showResetModal, resetToken]);

  // --- URL helpers ---
  const setAuthParam = (value) => {
    const p = new URLSearchParams(searchParams);
    p.set('auth', value);
    p.delete('token'); // clean token when switching away from reset
    setSearchParams(p); // push
  };

  const clearAuthParam = () => {
    const p = new URLSearchParams(searchParams);
    p.delete('auth');
    p.delete('token');
    setSearchParams(p); // push so back works
  };

  // --- Forgot submit ---
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim() });
      setForgotSent(true);
    } catch (err) {
      if (err.response?.status === 429) {
        setForgotError('Too many attempts. Please try again later.');
      } else {
        setForgotError('Something went wrong. Please try again.');
      }
    } finally {
      setForgotLoading(false);
    }
  };

  // --- Reset password submit ---
  const resetMismatch = resetPassword && resetConfirm && resetPassword !== resetConfirm;
  const resetTooShort = resetPassword && resetPassword.length < 6;

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (resetMismatch || resetTooShort) return;
    setResetLoading(true);
    setResetError('');
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, newPassword: resetPassword });
      // Success → switch to login
      setAuthParam('login');
      toast.success('Password updated successfully');
    } catch (err) {
      const detail = normalizeErrorMessage(err, '');
      if (err.response?.status === 429) {
        setResetError('Too many attempts. Please try again later.');
      } else if (detail.includes('Invalid') || detail.includes('expired')) {
        setResetError('invalid_or_expired');
      } else {
        setResetError(detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const features = [
    {
      icon: <QrCode className="h-8 w-8" />,
      title: "QR Code Tracking",
      description: "Track project specifications and on-site details with unique QR codes"
    },
    {
      icon: <FolderKanban className="h-8 w-8" />,
      title: "Project Management",
      description: "Organize and manage multiple construction projects efficiently"
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Team Collaboration",
      description: "Collaborate with teams and subcontractors in real time"
    },
    {
      icon: <Eye className="h-8 w-8" />,
      title: "No-Login Info Access",
      description: "Share project information instantly—no login required"
    },
    {
      icon: <Share2 className="h-8 w-8" />,
      title: "Shared or Project-Only Use",
      description: "Control access with shared or project-specific visibility"
    }
  ];

  const openAuthModal = (mode) => {
    setAuthParam(mode);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img 
                src="/specard-logo.png" 
                alt="Specard Logo" 
                className="h-10 object-contain"
              />
              <span className="text-2xl font-bold text-[#FF6600]">
                Specard
              </span>
            </div>

            {/* Auth Buttons */}
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost"
                onClick={() => openAuthModal('login')}
                className="border border-orange-500 text-orange-600 bg-transparent hover:bg-orange-500 hover:text-white transition-colors duration-150"
              >
                Login
              </Button>
              <Button 
                onClick={() => openAuthModal('signup')}
                style={{ background: '#FF6600' }}
                className="text-white hover:opacity-90"
              >
                Sign Up
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
          Track and manage{' '}
          <span className="text-[#FF6600]">
            all project specifications
          </span>{' '}
          in one platform.
        </h1>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-10">
          Simplify construction projects with QR-based specs, tracking, and collaboration.
        </p>
        <div className="flex justify-center gap-4">
          <Button 
            size="lg"
            onClick={() => openAuthModal('signup')}
            style={{ background: '#FF6600' }}
            className="text-white hover:opacity-90 text-lg px-8 py-6"
          >
            Get Started Free
          </Button>
          <Button 
            size="lg"
            variant="ghost"
            onClick={() => document.getElementById('learn-more').scrollIntoView({ behavior: 'smooth' })}
            className="border border-orange-500 text-orange-600 bg-transparent hover:bg-orange-500 hover:text-white transition-colors duration-150 text-lg px-8 py-6"
          >
            Learn More
          </Button>
        </div>
      </section>

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-slate-200 text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: '#FF6600' }}>
                <div className="text-white">
                  {feature.icon}
                </div>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-sm text-slate-600">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Learn More Section */}
      <section id="learn-more" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-white rounded-2xl shadow-lg p-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-6 text-center">
            Why Choose Specard?
          </h2>
          <div className="grid md:grid-cols-2 gap-12 mt-12">
            <div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                Streamlined Workflow
              </h3>
              <p className="text-slate-600 mb-6">
                Specard digitizes and centralizes construction project specifications to streamline your entire workflow. 
                From project setup to on-site execution, track every detail with clarity and control.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#FF6600] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-slate-700">Real-time project updates and team collaboration</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#FF6600] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-slate-700">QR code access for instant specifications and project details</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#FF6600] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-slate-700">Structured spec organization across all scopes of work</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                Built for Teams
              </h3>
              <p className="text-slate-600 mb-6">
                Invite team members, share projects, and collaborate seamlessly across your construction ecosystem. 
                Whether you're a general contractor, subcontractor, or property owner, 
                Specard adapts to your workflow.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#FF6600] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-slate-700">Invite unlimited team members</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#FF6600] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-slate-700">Role-based access control</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#FF6600] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-slate-700">Share projects with external stakeholders</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-[#FF6600] rounded-2xl shadow-lg p-12 text-white">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Get in Touch</h2>
            <p className="text-xl text-white text-opacity-90">
              Have questions? We'd love to hear from you.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white bg-opacity-20 mb-4">
                <Mail className="h-8 w-8" />
              </div>
              <h3 className="font-semibold mb-2">Email</h3>
              <p className="text-white text-opacity-90">info@specard.net</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white bg-opacity-20 mb-4">
                <Headphones className="h-8 w-8" />
              </div>
              <h3 className="font-semibold mb-2">Support</h3>
              <p className="text-white text-opacity-90">support@specard.net</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white bg-opacity-20 mb-4">
                <MapPin className="h-8 w-8" />
              </div>
              <h3 className="font-semibold mb-2">Location</h3>
              <p className="text-white text-opacity-90">Manassas, Virginia</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-3 mb-4 md:mb-0">
              <img 
                src="/specard-logo.png" 
                alt="Specard Logo" 
                className="h-8 object-contain"
              />
              <span className="text-xl font-bold">Specard</span>
            </div>
            <div className="flex items-center gap-4 text-slate-400 text-sm">
              <a href="/cookie-policy" className="hover:text-white transition-colors" data-testid="footer-cookie-policy-link">Cookie Policy</a>
              <span>© 2025 Specard. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      <Dialog open={showAuthModal} onOpenChange={(open) => { if (!open) clearAuthParam(); }}>
        <DialogContent className="max-w-md p-0 overflow-hidden">
          <AuthPage 
            isModal={true} 
            initialMode={authMode}
            onClose={clearAuthParam}
            onForgotPassword={() => setAuthParam('forgot-password')}
            onSwitchMode={setAuthParam}
          />
        </DialogContent>
      </Dialog>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotModal} onOpenChange={(open) => { if (!open) clearAuthParam(); }}>
        <DialogContent className="max-w-md" data-testid="forgot-password-modal">
          <DialogHeader>
            <DialogTitle className="text-center">Reset your password</DialogTitle>
            <DialogDescription className="text-center">
              {forgotSent ? 'Check your email' : 'Enter your email to receive a reset link'}
            </DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <div className="text-center space-y-4 py-4" data-testid="forgot-password-success">
              <div className="mx-auto w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Mail className="w-6 h-6 text-orange-600" />
              </div>
              <p className="text-sm text-gray-600">
                If this email is registered, you will receive a password reset link shortly.
              </p>
              <Button
                variant="outline"
                onClick={clearAuthParam}
                data-testid="forgot-close-btn"
              >
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotSubmit} className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email address</Label>
                <Input
                  id="forgot-email"
                  data-testid="forgot-email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={forgotEmail}
                  onChange={(e) => { setForgotEmail(e.target.value); setForgotError(''); }}
                  autoFocus
                  required
                />
              </div>
              {forgotError && (
                <p className="text-sm text-red-500" data-testid="forgot-error-msg">{forgotError}</p>
              )}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={clearAuthParam}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={forgotLoading || !forgotEmail.trim()}
                  data-testid="forgot-submit-btn"
                >
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showResetModal && !!resetToken} onOpenChange={(open) => { if (!open) clearAuthParam(); }}>
        <DialogContent className="max-w-md" data-testid="reset-password-modal">
          <DialogHeader>
            <DialogTitle className="text-center">
              {resetTokenValid === false || resetError === 'invalid_or_expired' ? 'Link Invalid' : 'Choose a new password'}
            </DialogTitle>
            <DialogDescription className="text-center">
              {resetTokenValid === null ? 'Verifying your reset link...' :
               resetTokenValid === false || resetError === 'invalid_or_expired' ? '' :
               'Enter and confirm your new password'}
            </DialogDescription>
          </DialogHeader>

          {/* Loading: validating token */}
          {resetTokenValid === null && (
            <div className="flex justify-center py-8" data-testid="reset-validating">
              <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
            </div>
          )}

          {/* Token invalid/expired/used */}
          {(resetTokenValid === false || resetError === 'invalid_or_expired') && (
            <div className="text-center space-y-4 py-4" data-testid="reset-token-expired">
              <p className="text-sm text-gray-600">
                This reset link is invalid or expired. Please request a new one.
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={() => setAuthParam('login')}
                  data-testid="reset-back-login-btn"
                >
                  Back to Login
                </Button>
                <Button
                  onClick={() => setAuthParam('forgot-password')}
                  data-testid="reset-go-forgot-btn"
                >
                  Request New Reset Link
                </Button>
              </div>
            </div>
          )}

          {/* Token valid → show form */}
          {resetTokenValid === true && resetError !== 'invalid_or_expired' && (
            <form onSubmit={handleResetSubmit} className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="reset-pw">New Password</Label>
                <PasswordInput
                  id="reset-pw"
                  data-testid="reset-password-input"
                  placeholder="Min. 6 characters"
                  value={resetPassword}
                  onChange={(e) => { setResetPassword(e.target.value); setResetError(''); }}
                  autoFocus
                  required
                />
                {resetTooShort && <p className="text-xs text-red-500" data-testid="reset-too-short">Must be at least 6 characters</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="reset-confirm">Confirm Password</Label>
                <PasswordInput
                  id="reset-confirm"
                  data-testid="reset-confirm-input"
                  placeholder="Re-enter password"
                  value={resetConfirm}
                  onChange={(e) => { setResetConfirm(e.target.value); setResetError(''); }}
                  required
                />
                {resetMismatch && <p className="text-xs text-red-500" data-testid="reset-mismatch">Passwords do not match</p>}
              </div>
              {resetError && resetError !== 'invalid_or_expired' && (
                <p className="text-sm text-red-500" data-testid="reset-error-msg">{resetError}</p>
              )}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setAuthParam('login')}
                >
                  Back to Login
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={resetLoading || !resetPassword || !resetConfirm || !!resetMismatch || !!resetTooShort}
                  data-testid="reset-submit-btn"
                >
                  {resetLoading ? 'Updating...' : 'Reset Password'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
